import { useState } from 'react';
import SchemaForm, { FormField } from '../../components/Forms/SchemaForm';
import VerificationPageLayout from '../../components/Layout/VerificationPageLayout';
import VerificationResultDisplay from '../../components/Verification/VerificationResultDisplay';
import {
    deedsEnquiryService,
    deedsSchema,
    DeedsFormData,
    DeedsMatchItem,
    DeedsEnquiryResponse,
    buildMatchPayload,
} from '../../services/verificationSystemServices/deedsEnquiryService';
import {
    Box,
    Alert,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
} from '@mui/material';
import ActionButton from '../../components/Button/ActionButton';

const deedsOffices = [
    { label: 'ALL', value: 'ALL' },
    { label: 'BLOEMFONTEIN', value: 'BLOEMFONTEIN' },
    { label: 'CAPE TOWN', value: 'CAPE TOWN' },
    { label: 'JOHANNESBURG', value: 'JOHANNESBURG' },
    { label: 'KIMBERLEY', value: 'KIMBERLEY' },
    { label: 'KING WILLIAMS TOWN', value: 'KING WILLIAMS TOWN' },
    { label: 'MPUMALANGA', value: 'MPUMALANGA' },
    { label: 'PIETERMARITZBURG', value: 'PIETERMARITZBURG' },
    { label: 'PRETORIA', value: 'PRETORIA' },
    { label: 'UMTATA', value: 'UMTATA' },
    { label: 'VRYBURG', value: 'VRYBURG' },
    { label: 'LIMPOPO', value: 'LIMPOPO' },
];

const searchTypes = [
    { label: 'Individual Search', value: 'Individual' },
    { label: 'Company Search', value: 'Company' },
    { label: 'Erf Search', value: 'Erf' },
    { label: 'Title Deed Search', value: 'Title Deed' },
];

const DeedsEnquiry = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [matches, setMatches] = useState<DeedsMatchItem[]>([]);
    const [fetchingResultFor, setFetchingResultFor] = useState<string | null>(null);
    const [report, setReport] = useState<DeedsEnquiryResponse | null>(null);
    const [searchTypeInd, setSearchTypeInd] = useState<string>('IDNo');
    const [searchReference, setSearchReference] = useState<{ yourReference?: string; voucherCode?: string }>({});

    const fetchReport = async (match: DeedsMatchItem) => {
        setFetchingResultFor(match.enquiry_result_id);
        setError(null);
        try {
            const response = await deedsEnquiryService.getResult({
                enquiryId: parseInt(match.enquiry_id),
                enquiryResultId: parseInt(match.enquiry_result_id),
                searchTypeInd,
                yourReference: searchReference.yourReference,
                voucherCode: searchReference.voucherCode,
            });
            setReport(response);
            setMatches([]);
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.message || 'Failed to fetch Deeds Enquiry report.');
        } finally {
            setFetchingResultFor(null);
        }
    };

    const handleSearchSubmit = async (data: any) => {
        setLoading(true);
        setError(null);
        setMatches([]);
        setReport(null);

        const formData = data as DeedsFormData;
        const payload = buildMatchPayload(formData);

        setSearchTypeInd(payload.searchTypeInd);
        setSearchReference({
            yourReference: formData.yourReference || undefined,
            voucherCode: formData.voucherCode || undefined,
        });

        try {
            const response = await deedsEnquiryService.getMatch(payload);

            if (!response.success || response.matches.length === 0) {
                setError(response.message || 'No matching properties found.');
                return;
            }

            if (response.matches.length === 1) {
                await fetchReport(response.matches[0]);
            } else {
                setMatches(response.matches);
            }
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };

    const formSchema: FormField[] = [
        {
            name: 'searchType',
            label: 'Search Type',
            type: 'select',
            options: searchTypes,
            gridProps: { xs: 12, md: 6 },
            required: true,
            confirmReset: true,
        },
        {
            name: 'deedsOffice',
            label: 'Deeds Office',
            type: 'select',
            options: deedsOffices,
            gridProps: { xs: 12, md: 6 },
            required: true,
        },

        // Individual Fields
        {
            name: 'idNo',
            label: 'ID Number',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            visibleIf: (val) => val.searchType === 'Individual',
            required: false,
            maxLength: 13,
        },
        {
            name: 'firstName',
            label: 'First Name',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            visibleIf: (val) => val.searchType === 'Individual',
            required: false,
        },
        {
            name: 'surname',
            label: 'Surname',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            visibleIf: (val) => val.searchType === 'Individual',
            required: false,
        },

        // Company Fields
        {
            name: 'businessName',
            label: 'Business Name',
            type: 'text',
            gridProps: { xs: 12, md: 12 },
            visibleIf: (val) => val.searchType === 'Company',
            required: false,
        },
        { name: 'reg1', label: 'Reg1', type: 'text', gridProps: { xs: 12, md: 4 }, visibleIf: (val) => val.searchType === 'Company', required: false },
        { name: 'reg2', label: 'Reg2', type: 'text', gridProps: { xs: 12, md: 4 }, visibleIf: (val) => val.searchType === 'Company', required: false },
        { name: 'reg3', label: 'Reg3', type: 'text', gridProps: { xs: 12, md: 4 }, visibleIf: (val) => val.searchType === 'Company', required: false },

        // Erf Fields
        {
            name: 'townshipName',
            label: 'Township Name',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            visibleIf: (val) => val.searchType === 'Erf',
            required: (val) => val.searchType === 'Erf',
        },
        {
            name: 'erfNo',
            label: 'Erf Number',
            type: 'text',
            gridProps: { xs: 12, md: 3 },
            visibleIf: (val) => val.searchType === 'Erf',
            required: (val) => val.searchType === 'Erf',
        },
        {
            name: 'portionNo',
            label: 'Portion Number',
            type: 'text',
            gridProps: { xs: 12, md: 3 },
            visibleIf: (val) => val.searchType === 'Erf',
            required: false,
        },

        // Title Deed Fields
        {
            name: 'titleDeedNo',
            label: 'Title Deed Number',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            visibleIf: (val) => val.searchType === 'Title Deed',
            required: (val) => val.searchType === 'Title Deed',
        },
        {
            name: 'bondAccountNo',
            label: 'Bond Account Number',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            visibleIf: (val) => val.searchType === 'Title Deed',
            required: false,
        },

        // Common Fields
        {
            name: 'yourReference',
            label: 'Your Reference',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
        {
            name: 'voucherCode',
            label: 'Voucher Code',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
    ];

    return (
        <VerificationPageLayout title="Deeds Enquiry">

            {/* Form is always visible */}
            <Box mb={4}>
                <SchemaForm
                    schema={formSchema}
                    onSubmit={handleSearchSubmit}
                    submitLabel={loading ? 'Searching…' : 'Search Deeds'}
                    validationSchema={deedsSchema}
                    defaultValues={{ searchType: 'Individual', deedsOffice: 'ALL' }}
                />
            </Box>

            {(loading || fetchingResultFor !== null) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                    <CircularProgress sx={{ color: '#2D4677' }} />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Multiple matches — let the user pick */}
            {matches.length > 1 && !report && (
                <Box mb={4}>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                        Select a property below to extract its full Deeds report.
                    </Typography>

                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #dce6f5', borderRadius: '12px', overflow: 'hidden' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#EFF3FB' }}>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Title Deed No</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Erf No</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Township</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Owner</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#132F5F', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Description</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {matches.map((match, idx) => (
                                    <TableRow
                                        key={idx}
                                        hover
                                        sx={{ '&:last-child td': { borderBottom: 'none' } }}
                                    >
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.title_deed_no || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.erf_no || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.township || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1a1a2e' }}>{match.owner_name || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.property_description || '—'}</TableCell>
                                        <TableCell align="right">
                                            <ActionButton
                                                size="small"
                                                disabled={fetchingResultFor !== null}
                                                onClick={() => fetchReport(match)}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    py: 0.75,
                                                    px: 2,
                                                }}
                                            >
                                                {fetchingResultFor === match.enquiry_result_id ? 'Fetching…' : 'Get Report'}
                                            </ActionButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Final report */}
            {report && !loading && !fetchingResultFor && (
                <>
                    {matches.length === 0 && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => { setReport(null); setMatches([]); }}
                            sx={{
                                mb: 2,
                                borderColor: '#2D4677',
                                color: '#2D4677',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': { borderColor: '#1a365d', backgroundColor: 'rgba(45,70,119,0.04)' },
                            }}
                        >
                            ← Clear Result
                        </Button>
                    )}
                    <VerificationResultDisplay
                        title="Deeds Enquiry"
                        requestId={report.log_id}
                        success={report.success}
                        message={report.message}
                        data={report.report_data ?? null}
                    />
                </>
            )}

        </VerificationPageLayout>
    );
};

export default DeedsEnquiry;
